import { useState, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Zap, Check, AlertTriangle, Compass, GitCompare } from 'lucide-react';
import { useLocation } from '@/shared/hooks/useLocation';
import { useToast } from '@/shared/ui/Toast';
import { strategyApi, type InputChange, type StrategyItem } from '../api/strategyApi';
import { lifecycleApi, type LifecycleData } from '../api/lifecycleApi';
import { uploadApi } from '../api/uploadApi';
import { Badge, Button, Card, Stat, SectionLabel, ProgressBar } from '@/shared/ui';
import { useWorkflowStatus, workflowsKey } from '@/shared/hooks/useWorkflows';
import { strategyKeys } from '../queryKeys';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { ConfirmDialog } from '@/shared/ui/Modal';
import { monthlyBudgetOf, oneTimeCostOf } from '../utils/strategyBudget';
import styles from './InsightsStep.module.css';

interface WizardContext {
  lifecycle?: LifecycleData;
  locationId: string;
  targetVersion?: number;
  goToStep: (step: number) => void;
  registerOnBeforeNext: (fn: (() => void | Promise<void>) | null) => void;
}

const MAX_CHANGES_SHOWN = 6;

/** A short list of input changes, e.g. "Market radius — 10 → 15 mi". */
function ChangeList({ changes }: { changes: InputChange[] }) {
  if (changes.length === 0) return null;
  const shown = changes.slice(0, MAX_CHANGES_SHOWN);
  const more = changes.length - shown.length;
  return (
    <ul className={styles.changeList}>
      {shown.map((c, i) => (
        <li key={`${c.kind}-${c.label}-${i}`}>
          <span className={styles.changeLabel}>{c.label}</span> — {c.detail}
        </li>
      ))}
      {more > 0 && <li>+{more} more</li>}
    </ul>
  );
}

export function InsightsStep() {
  const navigate = useNavigate();
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();
  const { success: toastSuccess, error: toastError } = useToast();
  const { targetVersion, lifecycle: wizardLifecycle } = useOutletContext<WizardContext>();
  const [exp, setExp] = useState(-1);
  const [approved, setApproved] = useState<Set<number>>(new Set());
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);

  // Insights query is scoped to the version being viewed/edited so switching
  // ?version=N triggers a fresh fetch instead of returning the cached row.
  const insightsQueryKey = strategyKeys.insights(locationId, targetVersion);

  const { data: insightsRes, isLoading } = useQuery({
    queryKey: insightsQueryKey,
    queryFn: () => strategyApi.getInsights(locationId, targetVersion),
    enabled: !!locationId,
    // Always refetch on mount — backend computes `stale` by comparing input
    // timestamps against generatedAt; cached responses from earlier in the
    // wizard would miss edits made on intervening steps.
    refetchOnMount: 'always',
  });

  const { data: lifecycleRes } = useQuery({
    queryKey: strategyKeys.lifecycle(locationId),
    queryFn: () => lifecycleApi.getLifecycle(locationId),
    enabled: !!locationId,
    initialData: wizardLifecycle ? { success: true as const, data: wizardLifecycle } : undefined,
  });
  // True when the version being edited is the currently-active one — clicking
  // "Regenerate" on an active version forks a new draft (existing behavior in
  // generateInsights). A confirm guards against accidental regeneration.
  const targetIsActive =
    targetVersion !== undefined && lifecycleRes?.data?.active?.version === targetVersion;

  const { data: enrichmentRes } = useQuery({
    queryKey: strategyKeys.enrichment(locationId),
    queryFn: () => uploadApi.getEnrichment(locationId),
    enabled: !!locationId,
  });

  const { data: brandProfileRes } = useQuery({
    queryKey: strategyKeys.brandProfile(locationId),
    queryFn: () => strategyApi.getBrandProfile(locationId),
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000,
  });
  const brandProfile = brandProfileRes?.data ?? null;

  const { data: brandPlatformRes } = useQuery({
    queryKey: strategyKeys.brandPlatform(locationId),
    queryFn: () => strategyApi.getBrandPlatform(locationId),
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000,
  });
  const platformPositioning = brandPlatformRes?.data?.content?.positioning ?? null;

  const {
    isRunning: insightsRunning,
    isLoading: workflowLoading,
    isFailed: lastRunFailed,
  } = useWorkflowStatus(
    locationId,
    'strategy_insights',
    [[...insightsQueryKey], [...strategyKeys.lifecycle(locationId)]],
    {
      onCompleted: () => toastSuccess('Strategies generated'),
      onFailed: (error) => toastError(error || 'Generation failed. Please try again.'),
    },
  );

  const {
    mutate: generate,
    isPending: generating,
    isError: generateRequestFailed,
    error: generateRequestError,
  } = useMutation({
    mutationFn: () => strategyApi.generateInsights(locationId, targetVersion),
    onSuccess: async (res) => {
      const newVersion = res?.data?.version;
      // Generation now runs in the background. Pick up its running workflow
      // before the mutation settles, so the page goes straight to the
      // "generating" state and watches it to completion.
      await queryClient.refetchQueries({ queryKey: workflowsKey(locationId) });
      queryClient.invalidateQueries({ queryKey: strategyKeys.lifecycle(locationId) });
      // Regeneration replaces the strategy list, so index-based approvals and locks
      // from the previous list no longer refer to anything. Clear both eagerly and
      // allow the sync effect to re-seed from whichever version the backend returns.
      setApproved(new Set());
      setLockedApprovals(new Set());
      syncedVersion.current = null;
      // If the backend forked a new draft (regenerate from an active version),
      // jump the URL to the new version so the wizard refetches that row's
      // insights instead of continuing to show the active version's snapshot.
      if (newVersion !== undefined && newVersion !== targetVersion) {
        navigate(`/strategy/builder/insights?version=${newVersion}`, { replace: true });
      }
    },
    onError: (err: unknown) => {
      queryClient.invalidateQueries({ queryKey: strategyKeys.lifecycle(locationId) });
      toastError((err as Error).message || 'Generation failed');
    },
  });

  const { mutate: activate, isPending: activating } = useMutation({
    mutationFn: (approvedIndexes: number[]) =>
      // generatedAt lets the server refuse approvals made against insights
      // that were regenerated since this page loaded them.
      lifecycleApi.completeStrategy(
        locationId,
        approvedIndexes,
        targetVersion,
        insights?.generatedAt ?? undefined,
      ),
    onSuccess: async (_res, approvedIndexes) => {
      await queryClient.invalidateQueries({ queryKey: strategyKeys.lifecycle(locationId) });
      await queryClient.invalidateQueries({ queryKey: strategyKeys.insights(locationId) });
      // Lock the just-activated indexes so the UI reflects them as locked
      // approvals without remounting the page.
      setLockedApprovals(new Set(approvedIndexes));
      toastSuccess('Strategy activated!');
    },
    onError: (err: unknown) => {
      queryClient.invalidateQueries({ queryKey: strategyKeys.insights(locationId) });
      toastError((err as Error).message || 'Activation failed');
    },
  });

  const insights = insightsRes?.data;
  // Shown in stored order: approvals are indexes into the stored list, so a
  // client-side re-sort could approve different strategies than the ones
  // clicked. The backend already orders new generations by revenue impact.
  const strats = insights?.strategies ?? [];
  const summary = insights?.summary;
  const enrichment = enrichmentRes?.data;
  const isBasic = (insights?.enrichmentLevel ?? enrichment?.level) === 'basic';

  // Compute summary metrics: aggregate approved strategies if any are approved, otherwise all
  const sourceStrats = approved.size > 0 ? strats.filter((_, i) => approved.has(i)) : strats;

  const monthlyBudget = (() => {
    // Recurring spend only — one-time setup costs aren't monthly budget.
    const total = sourceStrats.reduce((sum, s) => sum + monthlyBudgetOf(s), 0);
    return `$${total.toLocaleString('en-US')}`;
  })();

  // Mirrors backend computeImpactTier: ≥10% Exceptional, ≥3% Very Good, else Good
  const impactTier = (
    revMidpoint: number,
    baseline: number,
  ): 'Exceptional' | 'Very Good' | 'Good' => {
    if (baseline <= 0) return 'Good';
    const pct = revMidpoint / baseline;
    if (pct >= 0.1) return 'Exceptional';
    if (pct >= 0.03) return 'Very Good';
    return 'Good';
  };

  const dynamicSummary = (() => {
    if (!summary) return null;
    let patientsLow = 0,
      patientsHigh = 0,
      revLow = 0,
      revHigh = 0;
    for (const s of sourceStrats) {
      patientsLow += s.patients_low ?? 0;
      patientsHigh += s.patients_high ?? 0;
      revLow += s.revenue_low ?? 0;
      revHigh += s.revenue_high ?? 0;
    }
    const baseline = insights?.practiceRevenue ?? 0;
    const totalRevenueImpact =
      baseline > 0 ? impactTier((revLow + revHigh) / 2, baseline) : summary.totalRevenueImpact;
    return {
      totalRevenueImpact,
      totalPatientsRange: `${patientsLow}–${patientsHigh}`,
    };
  })();

  // Initialize approved set from backend data and track which are locked (already persisted).
  // Keyed on the strategy version rather than a one-shot flag: approvals are stored by index
  // into that version's strategy list, so a different version's indexes are meaningless here.
  // Regenerating from an active version forks a new draft (with no approvals) without
  // remounting this page — re-seeding on the version change stops the previous version's
  // locks from being applied by index to the freshly generated strategies.
  const syncedVersion = useRef<number | null>(null);
  const [lockedApprovals, setLockedApprovals] = useState<Set<number>>(new Set());
  useEffect(() => {
    const version = insights?.version;
    if (version === undefined || !insights?.approvedIndexes) return;
    if (syncedVersion.current === version) return;
    syncedVersion.current = version;
    const backendSet = new Set(insights.approvedIndexes);
    setApproved(backendSet);
    setLockedApprovals(backendSet);
  }, [insights?.version, insights?.approvedIndexes]);

  // Auto-generate on mount if insights are missing or stale (>90 days)
  const STALE_DAYS = 90;
  const autoTriggered = useRef(false);

  const needsGeneration = (() => {
    if (isLoading) return false;
    if (!insights || strats.length === 0) return true;
    if (!insights.generatedAt) return true;
    const age = Date.now() - new Date(insights.generatedAt).getTime();
    return age > STALE_DAYS * 24 * 60 * 60 * 1000;
  })();

  // Never auto-generate from the active version: that forks a refresh draft
  // and spends an AI call just because someone opened the page. The stale
  // banner and the Regenerate button (with its confirm) cover that case.
  // Nor after a failed run: the page shows the failure with Retry, instead
  // of a spinner that never ends and a new paid run on every reload.
  const generationFailed = lastRunFailed || generateRequestFailed;
  const autoGenerate = needsGeneration && !targetIsActive && !generationFailed;

  useEffect(() => {
    if (
      autoGenerate &&
      !generating &&
      !insightsRunning &&
      !workflowLoading &&
      !autoTriggered.current &&
      locationId
    ) {
      autoTriggered.current = true;
      generate();
    }
  }, [autoGenerate, generating, insightsRunning, workflowLoading, locationId]);

  const toggleApprove = (i: number) => {
    if (lockedApprovals.has(i)) return;
    const next = new Set(approved);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    setApproved(next);
  };

  const approveAll = () => setApproved(new Set(strats.map((_, i) => i)));
  const approvedCount = approved.size;
  const canActivate = approvedCount >= 1;
  const newApprovalCount = [...approved].filter((i) => !lockedApprovals.has(i)).length;

  const handleRegenerateClick = () => {
    if (targetIsActive) {
      setRegenerateConfirmOpen(true);
    } else {
      generate();
    }
  };

  const revenueImpactTone = (impact: string): 'accent' | 'info' | 'warning' => {
    if (impact === 'Exceptional') return 'accent';
    if (impact === 'Very Good') return 'info';
    return 'warning';
  };

  if (
    isLoading ||
    workflowLoading ||
    generating ||
    insightsRunning ||
    (!insights && autoGenerate)
  ) {
    return (
      <div className={styles.page}>
        <SectionLabel
          label="Strategic Insights & Recommendations"
          sub="AI-generated from practice data, market intelligence, and competitive analysis"
        />
        <div className={patterns.workflowCard}>
          <div className={patterns.workflowSpinner} />
          <div className={patterns.workflowTitle}>Generating Strategy Insights</div>
          <div className={patterns.workflowSubtext}>
            Analyzing practice data, market intelligence, and competitive landscape. Check back in
            5–10 minutes.
          </div>
        </div>
      </div>
    );
  }

  if (!insights || strats.length === 0) {
    return (
      <div className={styles.page}>
        <SectionLabel
          label="Strategic Insights & Recommendations"
          sub="AI-generated from practice data, market intelligence, and competitive analysis"
        />
        <Card className={styles.errorCard} padding="none">
          <p className={styles.errorMsg}>
            {generationFailed
              ? (generateRequestError as Error | null)?.message ||
                'Strategy generation failed. Please try again.'
              : 'No strategies have been generated for this version yet.'}
          </p>
          <Button
            variant="primary"
            onClick={() => {
              autoTriggered.current = false;
              generate();
            }}
          >
            {generationFailed ? 'Retry' : 'Generate strategies'}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <SectionLabel
        label="Strategic Insights & Recommendations"
        sub="AI-generated from practice data, market intelligence, and competitive analysis"
        timestamp={insights?.generatedAt}
      />

      {insights?.stale && (
        <Card className={styles.warningCard} padding="sm">
          <div className={styles.warningRow}>
            <span className={styles.warningIcon}>
              <AlertTriangle size={14} aria-hidden />
            </span>
            <div className={styles.warningBody}>
              <div className={styles.warningTitle}>Your inputs have changed</div>
              <div className={styles.warningText}>
                {insights.inputChanges?.length
                  ? 'Since these strategies were generated:'
                  : 'Practice data, competitors, or other inputs were updated after these strategies were generated.'}{' '}
                Regenerate to incorporate the latest data.
              </div>
              <ChangeList changes={insights.inputChanges ?? []} />
            </div>
            <button
              onClick={() => setRegenerateConfirmOpen(true)}
              disabled={generating}
              className={styles.warningBtn}
            >
              {generating ? 'Regenerating…' : 'Regenerate'}
            </button>
          </div>
        </Card>
      )}

      {!targetIsActive && !!insights?.changesSinceLive?.changes.length && (
        <Card className={styles.infoCard} padding="sm">
          <div className={styles.warningRow}>
            <span className={styles.infoIcon}>
              <GitCompare size={14} aria-hidden />
            </span>
            <div className={styles.warningBody}>
              <div className={styles.warningTitle}>
                Changed since the live strategy (version {insights.changesSinceLive.version})
              </div>
              <div className={styles.warningText}>
                Activating this refresh uses these inputs. Discarding it puts the answers, radius,
                competitor picks and reports back.
              </div>
              <ChangeList changes={insights.changesSinceLive.changes} />
            </div>
          </div>
        </Card>
      )}

      {isBasic && (
        <Card className={styles.warningCard} padding="sm">
          <div className={styles.warningRowCompact}>
            <span className={styles.warningIcon}>
              <Zap size={14} aria-hidden />
            </span>
            <div>
              <span className={styles.warningLabelInline}>Basic intelligence level — </span>
              <span className={styles.warningTextInline}>
                strategies below use census + competitive data. Upload practice data to unlock
                higher-confidence, revenue-specific recommendations.
              </span>
            </div>
          </div>
        </Card>
      )}

      {summary && dynamicSummary && (
        <Card className={styles.summaryCard} padding="md">
          <div className={styles.summaryGrid}>
            <Stat
              label="Revenue Impact"
              value={dynamicSummary.totalRevenueImpact}
              sub={approved.size > 0 ? 'Approved strategies' : 'All strategies combined'}
              tone={revenueImpactTone(dynamicSummary.totalRevenueImpact)}
            />
            <div className={styles.summaryDivider} />
            <Stat
              label="New Patients / Year"
              value={dynamicSummary.totalPatientsRange}
              sub={approved.size > 0 ? 'From approved campaigns' : 'Across all campaigns'}
            />
            <div className={styles.summaryDivider} />
            <Stat
              label="Recommended Budget"
              value={monthlyBudget}
              sub={approved.size > 0 ? '/month (approved)' : '/month'}
              tone="info"
            />
          </div>
        </Card>
      )}

      {strats.map((s: StrategyItem, i: number) => {
        const isApproved = approved.has(i);
        const isLocked = lockedApprovals.has(i);

        const approveBtnClass = isApproved
          ? isLocked
            ? styles.approveBtnApprovedLocked
            : styles.approveBtnApproved
          : isLocked
            ? styles.approveBtnLocked
            : styles.approveBtn;

        const confidenceClass =
          s.confidence === 'HIGH'
            ? styles.confidenceHigh
            : s.confidence === 'LOW'
              ? styles.confidenceLow
              : styles.confidenceMed;

        return (
          <Card
            key={i}
            className={isApproved ? styles.strategyCardApproved : styles.strategyCard}
            padding="none"
          >
            <div className={exp === i ? styles.itemHeaderExpanded : styles.itemHeader}>
              <div onClick={() => setExp(exp === i ? -1 : i)} className={styles.itemLeft}>
                <Badge variant={s.type === 'OPPORTUNITY' ? 'primary' : 'error'}>{s.type}</Badge>
                <span className={styles.itemTitle}>{s.title}</span>
              </div>
              <div className={styles.itemRight}>
                {s.revenue_impact && (
                  <Badge
                    variant={
                      s.revenue_impact === 'Exceptional'
                        ? 'primary'
                        : s.revenue_impact === 'Very Good'
                          ? 'info'
                          : 'warning'
                    }
                  >
                    {s.revenue_impact}
                  </Badge>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleApprove(i);
                  }}
                  disabled={isLocked}
                  title={isLocked ? 'This strategy is active and cannot be removed' : undefined}
                  className={approveBtnClass}
                >
                  {isApproved ? (
                    <span className={styles.approveCheck}>
                      <Check size={12} aria-hidden /> Approved
                    </span>
                  ) : (
                    'Approve'
                  )}
                </button>
                <span
                  onClick={() => setExp(exp === i ? -1 : i)}
                  className={exp === i ? styles.chevronOpen : styles.chevron}
                >
                  ▾
                </span>
              </div>
            </div>
            {exp === i && (
              <div className={styles.detailGrid}>
                <div>
                  <div className={styles.fieldLabel}>Target Segment</div>
                  <div className={styles.fieldValue}>{s.target_segment}</div>
                  <div className={styles.fieldLabel}>Data-Backed Rationale</div>
                  <div className={styles.fieldValueBody}>{s.rationale}</div>
                  <div className={styles.pillRow}>
                    <span className={styles.infoPill}>
                      <span className={styles.infoPillLabel}>Confidence: </span>
                      <span className={confidenceClass}>{s.confidence}</span>
                    </span>
                    {s.patients_per_year && s.patients_per_year !== 'Indirect' && (
                      <span className={styles.infoPill}>
                        <span className={styles.infoPillLabel}>Patients/yr: </span>
                        <span className={styles.infoPillValue}>{s.patients_per_year}</span>
                      </span>
                    )}
                    {s.roi_range && (
                      <span className={styles.infoPill}>
                        <span className={styles.infoPillLabel}>ROI: </span>
                        <span className={styles.infoPillValueAccent}>{s.roi_range}</span>
                      </span>
                    )}
                    {(monthlyBudgetOf(s) > 0 || oneTimeCostOf(s) === 0) && (
                      <span className={styles.infoPill}>
                        <span className={styles.infoPillLabel}>Budget: </span>
                        <span className={styles.infoPillValueInfo}>
                          ${monthlyBudgetOf(s).toLocaleString()}/mo
                        </span>
                      </span>
                    )}
                    {oneTimeCostOf(s) > 0 && (
                      <span className={styles.infoPill}>
                        <span className={styles.infoPillLabel}>One-time: </span>
                        <span className={styles.infoPillValueInfo}>
                          ${oneTimeCostOf(s).toLocaleString()}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>Recommended Actions</div>
                  {(s.actions ?? []).map((a, j) => (
                    <div key={j} className={styles.actionRow}>
                      <span className={styles.actionArrow}>→</span> {a}
                    </div>
                  ))}
                  {(s.prerequisites ?? []).length > 0 && (
                    <>
                      <div className={styles.fieldLabelSpaced}>Prerequisites</div>
                      {(s.prerequisites ?? []).map((p, j) => (
                        <div key={j} className={styles.prereqRow}>
                          <AlertTriangle size={12} aria-hidden /> {p}
                        </div>
                      ))}
                    </>
                  )}
                  {(s.primary_channels ?? []).length > 0 && (
                    <div className={styles.channelRow}>
                      {(s.primary_channels ?? []).map((ch) => (
                        <span key={ch} className={styles.channelPill}>
                          {ch.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>
        );
      })}

      {insights.budgetAllocationRationale && (
        <Card className={styles.rationaleCard} padding="md">
          <div className={patterns.fieldLabel}>Budget Allocation Rationale</div>
          <div className={styles.rationaleText}>{insights.budgetAllocationRationale}</div>
        </Card>
      )}

      {/* Approve & Activate bar */}
      <Card className={canActivate ? styles.activateCardActive : styles.activateCard} padding="md">
        <div className={styles.activateHeader}>
          <div>
            <div className={styles.approvedCount}>
              {approvedCount} of {strats.length} strategies approved
            </div>
            <div className={styles.approvedSub}>
              Approved strategies power SEO, ad campaigns, content, and keyword targeting.
            </div>
          </div>
          <div className={styles.activateActions}>
            {approvedCount < strats.length && (
              <Button variant="secondary" size="sm" onClick={approveAll}>
                Approve All
              </Button>
            )}
            <button
              onClick={handleRegenerateClick}
              disabled={generating}
              className={styles.regenerateBtn}
            >
              {generating ? 'Regenerating...' : 'Regenerate'}
            </button>
          </div>
        </div>
        <ProgressBar value={approvedCount} max={Math.max(strats.length, 1)} height={5} />

        {!targetIsActive && canActivate && (
          <div className={styles.activateCta}>
            <Button variant="primary" onClick={() => activate([...approved])} disabled={activating}>
              {activating ? 'Activating...' : 'Activate Strategy →'}
            </Button>
          </div>
        )}

        {targetIsActive && newApprovalCount >= 1 && (
          <div className={styles.activateCta}>
            <Button variant="primary" onClick={() => activate([...approved])} disabled={activating}>
              {activating
                ? 'Activating...'
                : `Activate ${newApprovalCount} Additional ${newApprovalCount === 1 ? 'Strategy' : 'Strategies'} →`}
            </Button>
            <div className={styles.activateNote}>
              Strategies already active and campaigns already launched keep running. Campaign
              recommendations and drafts you haven't launched yet are replaced with fresh ones that
              include the new strategies, and your SEO content plan is refreshed.
            </div>
          </div>
        )}
      </Card>

      {/* ── Supporting Foundation ─────────────────────────────────────────── */}
      {brandProfile && (
        <>
          <div className={styles.foundationHeader}>
            <span className={styles.foundationLabel}>Supporting foundation</span>
            <div className={styles.foundationRule} />
          </div>

          <Card className={styles.foundationCard} padding="md">
            <div className={styles.brandRow}>
              <div className={styles.brandBody}>
                <div className={styles.brandKicker}>
                  <Compass size={13} className={styles.brandKickerIcon} aria-hidden />
                  <span className={styles.brandKickerLabel}>Brand &amp; positioning</span>
                </div>

                <div className={styles.brandBadgeWrap}>
                  <Badge variant="primary">{brandProfile.label}</Badge>
                </div>

                {platformPositioning && (
                  <p className={styles.brandPositioning}>{platformPositioning}</p>
                )}

                <div className={styles.brandCta}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate('/strategy/brand-positioning')}
                  >
                    See Detailed Brand &amp; Positioning →
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </>
      )}

      <ConfirmDialog
        isOpen={regenerateConfirmOpen}
        onClose={() => setRegenerateConfirmOpen(false)}
        onConfirm={() => {
          setRegenerateConfirmOpen(false);
          generate();
        }}
        title="Regenerate strategy recommendations?"
        message="This creates a refresh draft with a fresh set of recommendations based on your latest practice data, market intelligence and competitive analysis. Your current strategy stays live until you activate the refresh — activating it replaces the current strategy and its approvals."
        confirmLabel="Regenerate"
        cancelLabel="Cancel"
        isLoading={generating}
      />
    </div>
  );
}
